<template>
  <div class="cm-contacts">
    <div class="cm-contacts-box-header">
      <h1>Select Contacts</h1>
      <v-icon large color="red" @click="$emit('close')">mdi-close</v-icon>
    </div>
    
    <div v-show="showEdit" class="cm-edit-form">
      <div class="cm-edit-form-container">
        <ContactForm :readonly="recordToEdit.type == 'Internal'" :edit="recordToEdit" @close="showEdit = false" @updated="contactFormSumbit" />
      </div>
    </div>

    <div class="cm-contacts-container">
        <!-- available contacts list -->
        
        <ContactList :items="unselected" :loading="loading > 0" emptytext="No contacts found."  listheight="470px" :highlight="highlighted.map(i => i.id)" @selected="toggle" @ctrlselected="toggleAdd">
          <template v-slot:header>
            <div class="cm-header-menu">
              <p class="section-title">My contacts</p>
              <v-icon color="green" :disabled="loading > 0" @click="createNewContact">mdi-account-plus</v-icon>
              <v-icon :color="editNext ? 'blue' : 'gray'" :disabled="loading > 0" @click="editNext = !editNext">mdi-pencil</v-icon>
              <v-icon :color="showInternal ? 'blue' : 'gray'" @click="showInternal = !showInternal">mdi-account-arrow-left</v-icon>
              <v-icon :color="showExternal ? 'red' : 'gray'" @click="showExternal = !showExternal">mdi-account-arrow-right</v-icon>
            </div>
          </template>
        
          <template v-slot:listheader>
            <div style="padding: 10px; width: 260px">
              <v-text-field v-model="search" placeholder="search..." append-icon="mdi-magnify" />
            </div>
          </template>
        </ContactList>

        <!-- selected contacts list -->
        <div>
          <ContactList :items="selected" :loading="loading > 0" emptytext="No contacts selected." listheight="470px" :highlight="highlightedSelected.map(i => i.id)" @selected="toggle" @ctrlselected="toggleRemove">
            <template v-slot:header>
              <div class="cm-header-menu">
                <p class="section-title">Selected</p>
              </div>
            </template>
          </ContactList>
        </div>
    </div>

    <div class="cm-actions">
      <v-btn @click="$emit('close')">cancel</v-btn>
      <v-btn color="primary" @click="submit">submit</v-btn>
    </div>
  </div>
</template>

<script>

import ContactForm from "./contactForm.vue"
import ContactList from "./contactsList.vue"

import { GetContacts } from "@/services/API"

// logger
import { Logger } from "aws-amplify"
const logger = new Logger("+++ contacts")

export default {
  components: { ContactForm, ContactList }, 

  props: {
    value: {
      type: Array,
      required: true
    },
    orgId: {
      type: String,
      required: false,
      default: null
    }
  },

  data () {
    return {
      contacts: [],
      selectedList: [], // just the id list of selected users
      highlighted: [],
      highlightedSelected: [],
      loading: 0,
      // filters and search
      search: '',
      showInternal: true,
      showExternal: true,
      // edit options
      recordToEdit: false,
      showEdit: false,
      editNext: false
    }
  },

  computed: {
    org() {
      if (this.$route.params.name) {
        return this.$route.params.name;
      }
      return this.orgId;
    },
    // last selected item
    filteredSet () {
      return this.contacts.filter(contact => {
        if(!this.showExternal && contact.type == "external") {
          return false
        }

        if(!this.showInternal && contact.type == "Internal") {
          return false
        }

        if(!contact.lastName && !contact.firstName) {
          return false
        }
        
        if(this.search.length) {
          return `${contact.firstName} ${contact.lastName}`.toLowerCase().includes(this.search.toLowerCase())
        }
        
        return true
      })
    },

    // a map object details the selected ids
    selectedMap () {
      return this.selectedList.reduce((m, email) => {
        m[email] = true
        return m
      }, {})
    },

    // currently selected contacts
    selected () {
      return this.contacts.filter(contact => this.selectedMap[contact.email]).sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()

        return nameA < nameB
          ? - 1
          : 1
      })
    },

    // available pool of unselected contacts
    unselected () {
      return this.filteredSet.filter(i => !this.selectedMap[i.email]).sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()

        return nameA < nameB
          ? - 1
          : 1
      })
    },
  },

  watch: {
    value (val) {
      logger.debug("### value was updated", val)
      this.selectedList = val
    }
  },

  mounted () {
    this.getContacts()
    this.selectedList = this.value
  },

  methods: {
    // adds a contact to selected
    toggle (contact) { 
      this.highlighted = []
      this.highlightedSelected = []

      if(this.editNext) {
        this.showEdit = true
        this.recordToEdit = contact
        this.editNext = false
        return
      }

      const index = this.selectedList.indexOf(contact.email)

      logger.debug("select", index)

      return this.selectedMap[contact.email]
        ? this.selectedList.splice(index, 1)
        : this.selectedList.push(contact.email) 
    },

    // adds a ctrl clicked set of contacts to selected
    toggleAdd (contact) {
      const index = this.highlighted.indexOf(contact)

      if(index != -1) {
        this.highlighted.splice(index, 1)
        return
      } 

      this.highlighted.push(contact)

      if(this.highlighted.length > 1) {
        const start = this.unselected.find(i => this.highlighted[0].id == i.id)
        const end = this.unselected.find(i => this.highlighted[1].id == i.id)
        const startIndex = this.unselected.indexOf(start)
        const endIndex = this.unselected.indexOf(end)
        const indexes = [startIndex, endIndex].sort()

        this.unselected.slice(indexes[0], indexes[1] + 1).forEach(contact => {
          this.selectedList.push(contact.email)
        })
        
        this.highlighted = []
      }    
    },

    // removes a ctrl clicked set of contacts from selected
    toggleRemove (contact) {
      const index = this.highlightedSelected.indexOf(contact)

      if(index != -1) {
        this.highlightedSelected.splice(index, 1)
        return
      } 

      this.highlightedSelected.push(contact)

      if(this.highlightedSelected.length > 1) {
        const start = this.selected.find(i => this.highlightedSelected[0].id == i.id)
        const end = this.selected.find(i => this.highlightedSelected[1].id == i.id)
        const startIndex = this.selected.indexOf(start)
        const endIndex = this.selected.indexOf(end)
        const indexes = [startIndex, endIndex].sort()

        this.selected.slice(indexes[0], indexes[1] + 1).forEach(contact => {
          this.selectedList.splice(this.selectedList.indexOf(contact.email), 1)
        })
        
        this.highlightedSelected = []
      }    
    },

    // creates a new record for us
    createNewContact () {
      this.recordToEdit = {
        id: false,
        firstName: '',
        lastName: '',
        telephone: '',
        mobile: '',
        email: '',
        tags: []
      }
      this.showEdit = true
    },

    // fetches the contact list
    async getContacts () {
      this.loading++
      logger.debug("+++ starting contacts search", this.org)
      const result = await GetContacts(this.org)

      if(result)
        this.contacts = result.Contacts
      
      logger.debug("+++ contacts return", result)
      this.loading--
    },

    contactFormSumbit () {
      this.getContacts()
      this.showEdit = false
    },

    // called when we confirm our selection
    submit () {
      this.$emit('input', this.selected.map(contact => contact.email))
      this.$emit('close')
    }
    
  }
}
</script>

<style lang="scss" scoped>
// contacts component
.cm-contacts {
  background: white;
  box-shadow: 3px 3px 3px rgba(0,0,0,0.2);
  position: relative;
  box-sizing: border-box;
  width: 900px;

  .cm-edit-form {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.5);
    display: flex;
    justify-content: space-around;
    align-items: center;
    z-index: 10;

    .cm-edit-form-container {
      border: 1px solid #2f2f2f;
    }
  }

  // box header
  .cm-contacts-box-header {
    padding: 10px 20px;
    width: 100%;
    background: #24AB80;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h1 {
      margin: 0;
      font-size: 18px !important;
      color: white;
    }
  }

  .cm-contacts-close {
    position: absolute;
    right: 12px;
    top: 40px;
  }

  .cm-contacts-container {
    display: flex;
    justify-content: space-around;
    position: relative;
  }
}

// confirm / cancel buttons displayed at end of form
.cm-actions {
  padding: 15px 70px 20px 40px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

// header menu displayed above user list
.cm-header-menu {
  display: flex;
  align-items: center;
  padding: 10px 0;

  .section-title {
    font-weight: bold !important;
    text-transform: uppercase !important;
    color: #2f2f2f !important;
    font-size: 16px;
    margin: 0 20px 0 0;
  }
}

// menus displayed above lists
.cm-contacts-list-menu {
  display: flex;
  width: 100%;
  align-items: center;

  h5 {
    margin: 0 15px 0 0;
  }
}
</style>